/**
 * CMMC Gaps + Requirements — V102-8-3 / V102-8-4 (ADR-0056; ux-adversary run
 * cmmc-self-assessment round 7 `gaps.web.html`, `requirements.web.html`; PR B
 * critic panel, rounds 1–2). Two views of the CMMC Prepare tab, beside the
 * scorecard (`?view=`):
 *
 *   Gaps          one hero per state (one named lever with its number split from
 *                 the sentence and the rest as chips; the conditional-status clock;
 *                 a blocked score; Level 1's count), then ONE list per kind: Not
 *                 assessed (Connect <source> only when it isn't connected, Collect
 *                 now when it is, Upload for 3.x.x, Request · results awaiting
 *                 review), Not met (must-Mets first, then POA&M-eligible with "Add
 *                 all"; each row's action follows its gap type: Add to POA&M,
 *                 Update the SSP, Re-check, new evidence), Met but stale.
 *   Requirements  every requirement expands to its objective × asset matrix
 *                 (AND rollup from the server); every cell opens its record, which
 *                 carries the same action as its Gaps row; the strategy panel sits
 *                 beside the list from 1100px, inline after the table below it.
 *                 Test key, version and hash sit behind Verify.
 *
 * Level 1 profiles: the 15 FAR requirements, no points, no POA&M; each opens
 * its linked Level 2 rows' objectives.
 *
 * Reads (any member; session workspace; a planned program 404s without the
 * preview): objective readiness, score, tests, determinations, stale list,
 * POA&M, gate, affirmations, posture connectors, findings, the evidence library.
 * Writes (existing endpoints only; each server-enforced, hidden or asked otherwise):
 *   Upload for 3.x.x   POST …/periods/:id/evidence — owner / admin / editor or the program lead.
 *   Add to POA&M       POST …/periods/:id/poam — the program lead or owner / admin.
 *   Collect now / Re-check  POST /posture-connectors/:id/run — owner / admin.
 * Recording from a cell (V102-8-5, CmmcRecord): record / revise / supersede (the objective's control owner,
 *   the program lead or owner / admin), pick the reviewer, confirm or send back (lead / owner / admin, never
 *   the recorder) — each server-enforced (403 / 403 `sod`).
 */
import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select, Spinner } from '@aigovops/design-system';
import type { AssessmentDetermination, PoamItem, ResolvedEvidenceRef } from '@aigovops/core';
import {
  getObjectiveReadiness, getProgramScore, getUnifiedEvidence, listDeterminations, listPostureConnectors, listProgramTests, listStaleResults, listWorkspaceFindings,
  openProgramPoamItem, runPostureConnector, uploadObjectiveEvidence, type ProgramCard,
} from '../../lib/api';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { fmtWithYear, rowAnchor } from '../../lib/cmmc-model';
import { useCmmcData } from '../../lib/use-cmmc';
import { focusAfterSettle, useFocusWhenMounted } from '../../lib/focus-settle';
import { programTitle } from '../../lib/nav-config';
import {
  STATUS, buildDrill, buildGaps, cellHistory, drillHeadlineStats, firstOpenCell, gapsHeroStats, heroFixLabel, plusPts,
  type AssessInput, type HeroStat, type RecordRow, type DrillCell, type DrillModel, type DrillObjective, type DrillRequirement, type GapFix, type GapRow, type GapsModel, type Lever,
} from '../../lib/cmmc-assess-model';
import { AskAdmin } from '../AskAdmin';
import { CellRecord } from './CmmcRecord';
import '../../pages/programs.css';
import '../../pages/today.css';
import '../../pages/cmmc.css';

export type CmmcView = 'scorecard' | 'requirements' | 'gaps';

const todayDay = () => new Date().toISOString().slice(0, 10);

export function useAssessData(slug: string, program: string, periodId: string) {
  const readiness = useQuery({ queryKey: queryKeys.objectiveReadiness(slug, program, periodId), queryFn: () => getObjectiveReadiness(slug, program, periodId), retry: false, staleTime: 60_000 });
  const score = useQuery({ queryKey: queryKeys.programScore(slug, program, periodId), queryFn: () => getProgramScore(slug, program, periodId), retry: false, staleTime: 30_000 });
  const tests = useQuery({ queryKey: queryKeys.programTests(slug, program, periodId), queryFn: () => listProgramTests(slug, program, periodId), retry: false, staleTime: 5 * 60_000 });
  const records = useQuery({ queryKey: queryKeys.determinations(slug, program, periodId, 'all'), queryFn: () => listDeterminations(slug, program, periodId), retry: false, staleTime: 30_000 });
  const stale = useQuery({ queryKey: queryKeys.programStale(slug, program, periodId), queryFn: () => listStaleResults(slug, program, periodId), retry: false, staleTime: 30_000 });
  const connectors = useQuery({ queryKey: queryKeys.postureConnectors(slug), queryFn: () => listPostureConnectors(slug), retry: false, staleTime: 60_000 });
  const findings = useQuery({ queryKey: queryKeys.findings(slug), queryFn: () => listWorkspaceFindings(slug), retry: false, staleTime: 60_000 });
  const cmmc = useCmmcData(slug, program, periodId);
  return { readiness, score, tests, records, stale, connectors, findings, gate: cmmc.gate, poam: cmmc.poam, affirmations: cmmc.affirmations };
}

/** The view switch: Scorecard · Requirements · Gaps (a sub-nav of the Prepare tab). */
export function CmmcSubnav({ view, counts }: { view: CmmcView; counts?: { reqs: number; gaps: number } | null }) {
  const { pathname } = useLocation();
  const item = (v: CmmcView, label: string) => (
    <li><Link to={v === 'scorecard' ? pathname : `${pathname}?view=${v}`} aria-current={view === v ? 'page' : undefined}>{label}</Link></li>
  );
  return (
    <nav className="gp-cmmc-subnav" aria-label="CMMC views" data-part="cmmc-subnav">
      <ul>
        {item('scorecard', 'Scorecard')}
        {item('requirements', counts ? `Requirements · ${counts.reqs}` : 'Requirements')}
        {item('gaps', counts ? `Gaps · ${counts.gaps}` : 'Gaps')}
      </ul>
    </nav>
  );
}

export interface AssessProps {
  slug: string;
  prefix: string;
  card: ProgramCard;
  periodId: string;
  periodLabel: string;
  /** owner / admin / editor or the program lead (the server enforces it). */
  canUpload: boolean;
  /** The program lead or owner / admin (POA&M, review). */
  canManage?: boolean;
  /** owner / admin (connector runs). */
  isAdmin?: boolean;
  viewerId: string;
  names?: ReadonlyMap<string, string>;
}

function useModels(p: Pick<AssessProps, 'slug' | 'card' | 'periodId'>) {
  const d = useAssessData(p.slug, p.card.program, p.periodId);
  const day = todayDay();
  const input = useMemo<AssessInput | null>(() => (d.readiness.data ? {
    readiness: d.readiness.data.readiness, score: d.score.data?.score ?? null, tests: d.tests.data?.tests ?? [], determinations: d.records.data?.determinations ?? [],
    stale: d.stale.data?.stale ?? [], poam: d.poam.data?.poam ?? [], gate: d.gate.data?.gate ?? null, affirmations: d.affirmations.data?.affirmations ?? [],
    connectors: d.connectors.data?.connectors ?? [], findings: d.findings.data?.findings ?? [], todayDay: day,
  } : null), [d.readiness.data, d.score.data, d.tests.data, d.records.data, d.stale.data, d.poam.data, d.gate.data, d.affirmations.data, d.connectors.data, d.findings.data, day]);
  const drill = useMemo(() => (input ? buildDrill(input) : null), [input]);
  const gaps = useMemo(() => (input && drill ? buildGaps(input, drill) : null), [input, drill]);
  return { d, drill, gaps };
}

/**
 * Today / Findings / Back links land here with #gap-<ref>, #req-<ref> or #cell-<key>:
 * open and focus it once rendered (F19: Back restores the row).
 */
function useHashFocus(ready: boolean, onTarget?: (id: string, el: HTMLElement) => void) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || !hash || hash.length < 2) return;
    const id = decodeURIComponent(hash.slice(1));
    const el = document.getElementById(id);
    if (!el) return;
    const details = (el.tagName === 'DETAILS' ? el : el.closest('details')) as HTMLDetailsElement | null;
    if (details) details.open = true;
    el.scrollIntoView?.({ block: 'start' });
    (el.tagName === 'DETAILS' ? el.querySelector<HTMLElement>('summary') ?? el : el).focus();
    onTarget?.(id, el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, hash]);
}

/** F10: the strategy panel sits beside the list from 1100px, inline below it. */
function useNarrow(px = 1100): boolean {
  const q = `(max-width: ${px - 1}px)`;
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(q).matches : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, [q]);
  return narrow;
}

/** PR #57 R1 aesthetic High #1: a stat row — large number + small caption, one tile per segment.
 * Body text stays in `var(--text)`; only the failing tile carries `data-tone="danger"`. */
function HeroStats({ stats }: { stats: readonly HeroStat[] }) {
  return (
    <ul className="gp-cmmc-stats" data-part="hero-stats">
      {stats.map((s, i) => (
        <li key={`${i}-${s.caption}`} className="gp-cmmc-stat" data-tone={s.tone ?? undefined}>
          {s.value ? <b>{s.value}</b> : null}
          {s.caption ? <em>{s.caption}</em> : null}
        </li>
      ))}
    </ul>
  );
}

function Loading({ q, label }: { q: { isLoading: boolean; error: unknown; refetch: () => unknown }; label: string }) {
  if (q.isLoading) return <Spinner label={`Loading ${label}`} />;
  return <Alert variant="error"><p>{`The ${label} could not load.`}</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
}

// V109-1-19 R3 (R2-H3): a screenshot (PNG / JPG) is the usual evidence for a setting GAC Pro can't read.
const ACCEPT = ['.pdf', '.doc', '.docx', '.md', '.txt', '.png', '.jpg', '.jpeg'];
const titleFromFile = (name: string) => name.replace(/\.[A-Za-z0-9]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 200);
const cellId = (key: string) => `cell-${key.replace(/[^A-Za-z0-9]+/g, '-')}`;
const objId = (id: string) => `obj-${id.replace(/[^A-Za-z0-9]+/g, '-')}`;
const scrollTo = (id: string) => (e: { preventDefault: () => void }) => { e.preventDefault(); const el = document.getElementById(id); el?.scrollIntoView?.({ block: 'center' }); el?.focus(); };

// ---- "Upload for 3.x.x" ------------------------------------------------------------------------

/**
 * V102-8-3: evidence uploaded FOR an objective — stored with its hash and the
 * objective's test, so the evaluator and the engagement view find it. Records
 * nothing: a person (or the evaluator) still records the result.
 */
export function UploadForObjective({ slug, program, periodId, refLabel, objectives, label, quiet, recorder, onUploaded, forLabel, primary, testKey, startOpen, buttonClassName }: {
  slug: string; program: string; periodId: string; refLabel: string; objectives: ReadonlyArray<{ id: string; ref: string }>; label?: string;
  /** A quiet text action beside the row's main action (one button per row). */
  quiet?: boolean;
  /** Who records it next ("Dana Okafor records it" / "Record it"). */
  recorder?: ReactNode;
  onUploaded?: (objectiveId: string) => void;
  /** The id the success line names (Level 1: the FAR id, not the Level 2 objective). */
  forLabel?: string;
  /** The hero's one primary (the last row to upload). */
  primary?: boolean;
  /**
   * V109-1-19 R3 (R2-H3): the test GAC Pro couldn't check — the upload is linked to it (pre-filled, not a
   * choice) and the confirmation says it counts from the next check.
   */
  testKey?: string | null;
  /** Open the form at once (arriving from a fix link). */
  startOpen?: boolean;
  /** The opener's class when it isn't one of the CMMC styles (e.g. a DS secondary button look). */
  buttonClassName?: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(!!startOpen);
  const [objective, setObjective] = useState(objectives[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const focusLater = useFocusWhenMounted();
  const idp = `up-${refLabel.replace(/[^A-Za-z0-9]+/g, '-')}`;
  // F17: opening focuses the file input; closing (Cancel, Escape, success) returns focus to "Upload for" once it is back.
  useEffect(() => { if (open) document.getElementById(`${idp}-file`)?.focus(); }, [open, idp]);
  const close = () => { setOpen(false); focusLater(() => opener.current); };
  const pickFile = (f: File | null) => {
    setFileErr(null);
    if (f && !ACCEPT.some((ext) => f.name.toLowerCase().endsWith(ext))) { setFile(null); setFileErr('Choose a PDF, Word, Markdown, text, PNG or JPG file.'); return; }
    setFile(f);
    if (f && !title.trim()) setTitle(titleFromFile(f.name));
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!file) { setFileErr(fileErr ?? 'Choose a file.'); document.getElementById(`${idp}-file`)?.focus(); return; }
    setBusy(true); setError(null); setFileErr(null);
    try {
      await uploadObjectiveEvidence(slug, program, periodId, { objectiveId: objective, file, title: title.trim() || undefined, testKey: testKey ?? null });
      await Promise.all([...invalidationMap['program:changed'](slug), ['objective-readiness', slug] as const, ['evidence-unified', slug] as const]
        .map((queryKey) => qc.invalidateQueries({ queryKey })));
      setDone(testKey
        ? `Uploaded for ${forLabel ?? objectives.find((o) => o.id === objective)?.ref ?? refLabel}. It clears “Couldn't check” at the next check`
        : `Uploaded for ${forLabel ?? objectives.find((o) => o.id === objective)?.ref ?? refLabel}`);
      onUploaded?.(objective);
      setFile(null); setTitle('');
      close();
    } catch (err) { setError(err instanceof Error ? err.message : 'The upload failed.'); }
    finally { setBusy(false); }
  };
  return (
    <span className="gp-cmmc-upload">
      {!open ? (
        <button ref={opener} type="button" className={buttonClassName ?? (primary ? 'gp-linkbtn btn-primary' : quiet ? 'gp-cmmc-quiet' : 'gp-linkbtn gp-linkbtn--compact')} onClick={() => { setOpen(true); setDone(null); }} data-part="upload-for"
          aria-label={quiet ? `Or upload evidence for ${refLabel}` : undefined}>{label ?? `Upload for ${refLabel}`}</button>
      ) : (
        <form className="gp-cmmc-cust-form gp-cmmc-upform" onSubmit={submit} noValidate aria-label={`Upload evidence for ${refLabel}`} data-part="upload-form"
          // R2-1: Escape closes the form only — it never reaches the panel around it.
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } }}>
          {error ? <Alert variant="error">{error}</Alert> : null}
          {objectives.length > 1 ? (
            <Field id={`${idp}-obj`} label="For">
              <Select value={objective} onChange={(e) => setObjective(e.currentTarget.value)}>
                {objectives.map((o) => <option key={o.id} value={o.id}>{o.ref}</option>)}
              </Select>
            </Field>
          ) : <p className="gp-cmmc-static">{`For ${objectives[0]?.ref ?? refLabel}`}</p>}
          {testKey ? <p className="gp-cmmc-static" data-part="upload-test">Linked to the test GAC Pro couldn't check</p> : null}
          <Field id={`${idp}-file`} label="File" required error={fileErr ?? undefined} hint="PDF, Word, Markdown, text, PNG or JPG · up to 25 MB">
            <Input type="file" accept={ACCEPT.join(',')} onChange={(e) => pickFile(e.currentTarget.files?.[0] ?? null)} />
          </Field>
          <Field id={`${idp}-title`} label="Title">
            <Input value={title} maxLength={200} onChange={(e) => setTitle(e.currentTarget.value)} />
          </Field>
          <span className="gp-actions">
            <Button type="submit" variant="secondary" aria-disabled={busy || undefined}>{busy ? 'Uploading…' : 'Upload'}</Button>
            <Button variant="ghost" onClick={close}>Cancel</Button>
          </span>
        </form>
      )}
      {/* Always mounted, so the success is announced (F17). */}
      <span role="status" className="gp-cmmc-note" data-part="upload-done">{done ? <>{`${done} · `}{recorder ?? 'waiting to be recorded'}</> : null}</span>
    </span>
  );
}

// ---- one row's action (F1 / F2) ------------------------------------------------------------------

interface RowCtx {
  p: AssessProps;
  base: string;
  /** The gaps URL with this row's anchor (every outgoing link returns here — F19). */
  back: (anchor: string) => string;
  recorder: ReactNode;
  onUploaded: (rowId: string) => void;
  /** N6: POA&M items added from this page (by requirement id). */
  added: ReadonlyMap<string, PoamItem | null>;
  markAdded: (rowId: string, item: PoamItem | null) => void;
  /** Focus once mounted, owned by the page (a row's button can unmount when the row turns into its POA&M item). */
  focusLater: (get: () => HTMLElement | null) => void;
}

/** Runs one or more connected sources (the row's Collect now / Re-check and the hero's group). */
function RunButton({ slug, runs, label, done, primary, ariaLabel }: {
  slug: string; runs: ReadonlyArray<{ connectorId: string; source: string }>; label: string; done: string; primary?: boolean; ariaLabel?: string;
}) {
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const run = async () => {
    if (state === 'busy') return;
    setState('busy');
    try {
      for (const r of runs) await runPostureConnector(slug, r.connectorId);
      setState('done');
      await qc.invalidateQueries({ queryKey: queryKeys.postureConnectors(slug) });
    } catch { setState('error'); }
  };
  const sources = runs.map((r) => r.source).join(', ');
  return (
    <span className="gp-cmmc-upload">
      <button type="button" className={primary ? 'gp-linkbtn btn-primary' : 'gp-linkbtn gp-linkbtn--compact'} aria-disabled={state === 'busy' || undefined} onClick={() => void run()}
        aria-label={ariaLabel ?? (label.includes(sources) ? label : `${label} · ${sources}`)} data-part="run-connector">
        {state === 'busy' ? 'Collecting…' : label}
      </button>
      <span role="status" className="gp-cmmc-note">{state === 'done' ? done : state === 'error' ? `${sources} could not start. Try again, or check the connection.` : ''}</span>
    </span>
  );
}

function PoamDone({ r, ctx, item }: { r: GapRow; ctx: RowCtx; item: PoamItem | null }) {
  const today = todayDay();
  return (
    <Link className="gp-cmmc-clock" data-part="poam-added" id={`poam-added-${r.anchor}`} to={`${ctx.base}#${rowAnchor(r.ref)}`}>
      {item?.closeout_due_at ? `On the POA&M · closeout ${fmtWithYear(item.closeout_due_at, today)}` : 'On the POA&M · planned'}
    </Link>
  );
}

function PoamButton({ r, ctx, determinationId, primary, label }: { r: GapRow; ctx: RowCtx; determinationId: string; primary?: boolean; label?: string }) {
  const { p } = ctx;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (ctx.added.has(r.id)) return <PoamDone r={r} ctx={ctx} item={ctx.added.get(r.id) ?? null} />;
  const add = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const res = await openProgramPoamItem(p.slug, p.card.program, p.periodId, { determination_id: determinationId });
      ctx.markAdded(r.id, res.item ?? null);
      // N6 / r5: focus stays on the row — its "On the POA&M" link (either form), else the row itself — even after the reload re-renders it.
      ctx.focusLater(() => document.getElementById(`poam-added-${r.anchor}`) ?? document.getElementById(r.anchor));
      await Promise.all([...invalidationMap['program:changed'](p.slug), queryKeys.programPoam(p.slug, p.card.program, p.periodId)].map((queryKey) => qc.invalidateQueries({ queryKey })));
      // r6: the refetch re-mounts the row as its POA&M item — focus it again once the data has settled.
      focusAfterSettle(() => document.getElementById(`poam-added-${r.anchor}`) ?? document.getElementById(r.anchor));
    } catch (e) { setErr(e instanceof Error ? e.message : 'It could not be added.'); }
    finally { setBusy(false); }
  };
  return (
    <span className="gp-cmmc-upload">
      <button type="button" className={primary ? 'gp-linkbtn btn-primary' : 'gp-linkbtn gp-linkbtn--compact'} aria-disabled={busy || undefined} onClick={() => void add()}
        aria-label={`Add ${r.ref} to the POA&M`} data-part="add-poam">
        {busy ? 'Adding…' : label ?? 'Add to POA&M'}
      </button>
      {err ? <span role="alert" className="gp-field-err">{err}</span> : null}
    </span>
  );
}

function FixAction({ fix, r, ctx, primary, label }: { fix: GapFix; r: GapRow; ctx: RowCtx; primary?: boolean; label?: string }) {
  const { p, base } = ctx;
  const cls = primary ? 'gp-linkbtn btn-primary' : 'gp-linkbtn gp-linkbtn--compact';
  const ret = encodeURIComponent(ctx.back(r.anchor));
  const ask = (verb: string, what: string, roles: string[]) => <AskAdmin slug={p.slug} compact verb={verb} what={what} subject={`GAC Pro: ${r.ref} ${r.title}`} roles={roles} exclude={p.viewerId} primary={primary} />;
  switch (fix.kind) {
    case 'connect':
      return p.isAdmin
        ? <Link className={cls} data-part="connect" to={`${p.prefix}/setup?connect=${encodeURIComponent(fix.connectorKind)}&back=${ret}&back_label=${encodeURIComponent(`${programTitle(p.card.program)} Gaps`)}`}>{label ?? fix.label}</Link>
        : ask(`connect ${fix.label.replace(/^Connect /, '')}`, `connects ${fix.label.replace(/^Connect /, '')}`, ['owner', 'admin']);
    case 'collect':
      return p.isAdmin ? <RunButton slug={p.slug} runs={[{ connectorId: fix.connectorId, source: fix.source }]} label={label ?? 'Collect now'} primary={primary} done={`Collecting from ${fix.source} · results appear when it finishes`} />
        : <span className="gp-cmmc-note">{`${fix.source} collects nightly`}</span>;
    case 'recheck':
      return p.isAdmin ? <RunButton slug={p.slug} runs={[{ connectorId: fix.connectorId, source: fix.source }]} label={label ?? `Re-check · ${fix.source}`} primary={primary} done={`Re-checking with ${fix.source}`} />
        : <span className="gp-cmmc-note">{`Re-check · ${fix.source} runs nightly`}</span>;
    case 'nightly':
      return <span className="gp-cmmc-note">{fix.label}</span>;
    case 'upload':
      return p.canUpload && r.uploadFor.length
        ? <UploadForObjective slug={p.slug} program={p.card.program} periodId={p.periodId} refLabel={r.ref} objectives={r.uploadFor} label={label ?? fix.label}
            recorder={ctx.recorder} onUploaded={() => ctx.onUploaded(r.id)} forLabel={r.l2Refs[0] === r.ref ? undefined : r.ref} />
        : ask('upload it', 'uploads evidence', ['owner', 'admin', 'editor']);
    case 'request':
      return ask('provide it', 'provides this evidence', ['owner', 'admin', 'editor']);
    case 'review':
      // The same review item the scorecard lists (F8).
      return <Link className={cls} data-part="review" to={`${base}#review-${fix.determinationId}`}>{label ?? fix.label}</Link>;
    case 'poam':
      return p.canManage ? <PoamButton r={r} ctx={ctx} determinationId={fix.determinationId} primary={primary} label={label} /> : ask('add it to the POA&M', 'adds it to the POA&M', ['owner', 'admin']);
    case 'poam_open':
      // R2-2: a primary keeps its CTA colours; the urgency is separate toned text on the page surface.
      return primary
        ? <span className="gp-cmmc-upload"><Link className="gp-linkbtn btn-primary" data-part="poam-item" to={`${base}#${rowAnchor(r.ref)}`}>{label ?? `Close ${r.ref}`}</Link><span className="gp-cmmc-toned" data-tone={fix.tone}>{fix.label}</span></span>
        : !fix.label ? null
          : <Link className="gp-cmmc-clock" data-tone={fix.tone} data-part="poam-item" id={`poam-added-${r.anchor}`} to={`${base}#${rowAnchor(r.ref)}`}>{fix.label}</Link>;
    case 'ssp':
      return <Link className={cls} data-part="ssp" to={`${p.prefix}/policies?new=ssp&for=${encodeURIComponent(r.l2Refs[0] ?? r.ref)}&return=${ret}`}>{label ?? fix.label}</Link>;
  }
}

/** NEW-5: one gap-type button per row, then the quiet "or upload" on the same line. */
function RowActions({ r, ctx }: { r: GapRow; ctx: RowCtx }) {
  const { p } = ctx;
  const quietUpload = (r.fix.kind === 'connect' || r.fix.kind === 'collect' || r.fix.kind === 'request') && p.canUpload && r.uploadFor.length
    ? <UploadForObjective slug={p.slug} program={p.card.program} periodId={p.periodId} refLabel={r.ref} objectives={r.uploadFor} label="or upload" quiet
        recorder={ctx.recorder} onUploaded={() => ctx.onUploaded(r.id)} forLabel={r.l2Refs[0] === r.ref ? undefined : r.ref} />
    : null;
  return <><FixAction fix={r.fix} r={r} ctx={ctx} />{r.then ? <FixAction fix={r.then} r={r} ctx={ctx} /> : null}{quietUpload}</>;
}

/** F1: "Open findings (n)" only when there are some. */
function FindingsLink({ r, ctx }: { r: GapRow; ctx: RowCtx }) {
  const { p } = ctx;
  if (!r.findings.length) return null;
  return <Link className="gp-cmmc-quiet" data-part="open-findings" to={`${p.prefix}/findings?requirement=${r.l2Refs.join(',')}&program=${encodeURIComponent(p.card.program)}&period=${encodeURIComponent(p.periodId)}&return=${encodeURIComponent(ctx.back(r.anchor))}`}>
    {`Open findings (${r.findings.length})`}</Link>;
}

/** G6 / N4: a hero lever acts — Connect links to Setup, Collect now runs every source in the group. */
function LeverAction({ lever, ctx, primary }: { lever: Lever; ctx: RowCtx; primary?: boolean }) {
  const { p } = ctx;
  const cls = primary ? 'gp-linkbtn btn-primary' : 'gp-linkbtn gp-linkbtn--compact';
  const label = `${lever.label}${plusPts(lever.pts)}`;
  const gapsUrl = `${ctx.base}?view=gaps`;
  if (lever.kind === 'connect') {
    return p.isAdmin
      ? <Link className={cls} data-part={primary ? 'primary' : 'lever'} to={`${p.prefix}/setup?connect=${encodeURIComponent(lever.connectorKind!)}&back=${encodeURIComponent(ctx.back(lever.first.anchor))}&back_label=${encodeURIComponent(`${programTitle(p.card.program)} Gaps`)}`}>{label}</Link>
      : <span data-part={primary ? 'primary' : 'lever'}><AskAdmin slug={p.slug} compact primary={primary} verb={lever.label.split(' · ')[0]!.toLowerCase()} what="connects it" subject={`GAC Pro: ${lever.label}`} roles={['owner', 'admin']} exclude={p.viewerId} /></span>;
  }
  if (lever.kind === 'collect') {
    const sources = (lever.run ?? []).map((x) => x.source).join(', ');
    return p.isAdmin
      ? <span data-part={primary ? 'primary' : 'lever'}><RunButton slug={p.slug} runs={lever.run ?? []} label={label} primary={primary} ariaLabel={label} done={`Collecting from ${sources} · results appear when it finishes`} /></span>
      : <span className="gp-cmmc-note" data-part={primary ? 'primary' : 'lever'}>{`${sources} collect nightly`}</span>;
  }
  // Upload / request: the group's rows (the hero) or the focused Evidence list, which links back here (a chip).
  if (lever.kind === 'upload' && primary && lever.count === 1 && p.canUpload && lever.first.uploadFor.length) {
    const r = lever.first;
    return <span data-part="primary"><UploadForObjective slug={p.slug} program={p.card.program} periodId={p.periodId} refLabel={r.ref} objectives={r.uploadFor} label={label} primary
      recorder={ctx.recorder} onUploaded={() => ctx.onUploaded(r.id)} forLabel={r.l2Refs[0] === r.ref ? undefined : r.ref} /></span>;
  }
  if (lever.kind === 'upload') {
    // r6: the way back lands on the group (or the one row) it came from.
    const back = lever.count === 1 ? ctx.back(lever.first.anchor) : `${gapsUrl}#gaps-ne`;
    return <Link className={cls} data-part={primary ? 'primary' : 'lever'} to={`${p.prefix}/evidence?program=${encodeURIComponent(p.card.program)}&period=${encodeURIComponent(p.periodId)}&need=unassessed&return=${encodeURIComponent(back)}`}>{label}</Link>;
  }
  return <a className={cls} href="#gaps-ne" data-part={primary ? 'primary' : 'lever'} onClick={scrollTo('gaps-ne')}>{label}</a>;
}

// ---- Gaps --------------------------------------------------------------------------------------

function useRowCtx(p: AssessProps, records: readonly AssessmentDetermination[] | undefined, onUploaded: (id: string) => void): RowCtx {
  const base = `${p.prefix}/programs/${p.card.program}/prepare`;
  const gapsUrl = `${base}?view=gaps`;
  const [added, setAdded] = useState<ReadonlyMap<string, PoamItem | null>>(new Map());
  const focusLater = useFocusWhenMounted();
  // Who records an uploaded file next: the viewer when they can, else whoever reviewed this program's latest result.
  const reviewer = [...(records ?? [])].filter((x) => x.reviewed_by && x.reviewed_at).sort((a, b) => (a.reviewed_at! < b.reviewed_at! ? 1 : -1))[0]?.reviewed_by ?? null;
  const name = reviewer ? p.names?.get(reviewer) ?? null : null;
  const recorder: ReactNode = p.canManage ? 'Record it in Requirements' : name ? `${name} records it` : 'The program lead records it';
  return {
    p, base, back: (anchor) => `${gapsUrl}#${anchor}`, recorder, onUploaded, added,
    markAdded: (id, item) => setAdded((m) => new Map([...m, [id, item]])), focusLater,
  };
}

function HeroPrimaryAction({ g, ctx, p }: { g: GapsModel; ctx: RowCtx; p: AssessProps }) {
  const pr = g.hero.primary;
  if (!pr) return null;
  if (pr.kind === 'audit') return <Link className="gp-linkbtn btn-primary" data-part="primary" to={`${p.prefix}/programs/${p.card.program}/audit`}>{pr.label}</Link>;
  if (pr.kind === 'fix') {
    const r = pr.row;
    const fix = pr.fix;
    // G16: on the clock states the primary DOES the fix; the status end stays beside it as toned text (R2-2: never on the button).
    const end = g.statusEnd && r.fix.kind === 'poam_open'
      ? <span className="gp-cmmc-toned" data-tone={g.statusEnd.tone} data-part="primary-clock">{g.statusEnd.days < 0 ? `closed by ${fmtWithYear(g.statusEnd.date, todayDay())} · passed` : `closes by ${fmtWithYear(g.statusEnd.date, todayDay())} · ${g.statusEnd.days} day${g.statusEnd.days === 1 ? '' : 's'}`}</span>
      : null;
    if (fix.kind === 'poam_open' || fix.kind === 'upload' || fix.kind === 'nightly') {
      // A row's own form lives on the row: the hero jumps to it (and opens nothing it can't finish here).
      return <span className="gp-cmmc-upload" data-part="primary-wrap"><a className="gp-linkbtn btn-primary" href={`#${r.anchor}`} data-part="primary" onClick={scrollTo(r.anchor)}>{pr.label}</a>{end}</span>;
    }
    return <span className="gp-cmmc-upload" data-part="primary-wrap"><span data-part="primary"><FixAction fix={fix} r={r} ctx={ctx} primary label={pr.label} /></span>{end}</span>;
  }
  return <LeverAction lever={pr.lever} ctx={ctx} primary />;
}

/** Add every listed POA&M-eligible row, after one confirm that names them (N6; lead / admin). */
function AddAllPoam({ rows, ctx }: { rows: GapRow[]; ctx: RowCtx }) {
  const { p } = ctx;
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const focusLater = useFocusWhenMounted();
  const todo = rows.filter((r) => r.fix.kind === 'poam' && !ctx.added.has(r.id));
  const cancel = () => { setConfirm(false); focusLater(() => document.querySelector<HTMLElement>('[data-part="add-all-poam"]')); };
  if (!p.canManage || todo.length < 2) return null;
  const run = async () => {
    setBusy(true); setErr(null);
    for (const r of todo) {
      if (r.fix.kind !== 'poam') continue;
      try { const res = await openProgramPoamItem(p.slug, p.card.program, p.periodId, { determination_id: r.fix.determinationId }); ctx.markAdded(r.id, res.item ?? null); }
      catch (e) { setErr(`${r.ref}: ${e instanceof Error ? e.message : 'it could not be added'}`); break; }
    }
    setBusy(false); setConfirm(false);
    focusLater(() => document.getElementById('gaps-poam'));
    await Promise.all([...invalidationMap['program:changed'](p.slug), queryKeys.programPoam(p.slug, p.card.program, p.periodId)].map((queryKey) => qc.invalidateQueries({ queryKey })));
  };
  return (
    <span className="gp-cmmc-addall">
      {!confirm
        ? <button type="button" className="gp-cmmc-quiet" data-part="add-all-poam" onClick={() => setConfirm(true)}>{`Add all ${todo.length} to POA&M`}</button>
        : (
          <span className="gp-cmmc-confirm" role="group" aria-label={`Add ${todo.length} to the POA&M`} data-part="add-all-confirm"
            onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}>
            <span className="gp-cmmc-note">{`Add ${todo.map((r) => r.ref).join(', ')} to the POA&M?`}</span>
            <span className="gp-cmmc-confirm-acts">
            <button type="button" className="gp-linkbtn gp-linkbtn--compact" aria-disabled={busy || undefined} onClick={() => void run()} data-part="add-all-yes" autoFocus>{busy ? 'Adding…' : `Add ${todo.length}`}</button>
            <button type="button" className="gp-cmmc-quiet" onClick={cancel}>Cancel</button>
            </span>
          </span>
        )}
      {err ? <span role="alert" className="gp-field-err">{err}</span> : null}
    </span>
  );
}

export function CmmcGaps(p: AssessProps) {
  const { d, gaps } = useModels(p);
  const [uploaded, setUploaded] = useState<ReadonlySet<string>>(new Set());
  const ctx = useRowCtx(p, d.records.data?.determinations, (id) => setUploaded((s) => new Set([...s, id])));
  useHashFocus(!!gaps);
  if (!gaps) return <Loading q={d.readiness} label="gaps" />;
  const base = ctx.base;
  const c = gaps.counts;
  const l1 = gaps.level === '1';
  const sum = (rows: GapRow[]) => rows.reduce((n, r) => n + (r.points ?? 0), 0);
  const ptsOf = (rows: GapRow[]) => (l1 ? '' : ` · ${sum(rows)} pts`);
  const row = (r: GapRow, meta: ReactNode, action: ReactNode, opts: { notMet?: boolean; tone?: string; noPts?: boolean } = {}) => (
    // H-R3-2: the clock tones only the 3px bar (data-clock), never the row's text.
    <li key={r.id} id={r.anchor} tabIndex={-1} data-gap={r.id} data-clock={opts.tone}>
      <span className="gp-cmmc-name">
        <Link to={`${base}?view=requirements#${r.reqAnchor}`} aria-label={`${r.title} · ${r.ref}`}>{r.title}</Link>
        <span className="gp-dim gp-cmmc-code" aria-hidden="true">{r.ref}</span>
        {/* F16 / G14: red "can't go on POA&M" only on Not met; "must-Met · unproven" (quiet) on Not assessed. Level 1: everything must be Met, so no tag. */}
        {!l1 && r.mustMet ? (opts.notMet ? <span className="gp-cmmc-never">can’t go on POA&amp;M</span> : <span className="gp-cmmc-mm">must-Met · unproven</span>) : null}
      </span>
      {/* A5: points in a fixed slot. */}
      <span className="gp-cmmc-pts">{!l1 && !opts.noPts && r.points !== null ? (r.points === 0 ? 'required' : `${r.points} pt${r.points === 1 ? '' : 's'}`) : ''}</span>
      <span className="gp-cmmc-meta">{meta}<FindingsLink r={r} ctx={ctx} /></span>
      <span className="gp-cmmc-act">{action}{uploaded.has(r.id) ? <span className="gp-cmmc-note" data-part="to-record">{ctx.recorder}</span> : null}</span>
    </li>
  );
  const nothingOpen = !c.needEvidence && !c.waiting && !c.notMet && !c.stale;
  const empty = [!c.needEvidence && !c.waiting ? 'nothing to collect' : null, !c.notMet ? 'nothing Not met' : null, !c.stale ? 'nothing stale' : null].filter(Boolean);
  const h = gaps.hero;
  // A14: zero entries are dropped.
  const countLine = [c.needEvidence ? `${c.needEvidence} need evidence` : null, c.waiting ? `${c.waiting} awaiting review` : null, c.notMet ? `${c.notMet} not met` : null, c.stale ? `${c.stale} Met stale` : null].filter(Boolean).join(' · ');
  const poamTone = gaps.statusEnd?.tone;
  const sameEnd = !!gaps.statusEnd && gaps.notMetPoam.length > 0 && gaps.notMetPoam.every((r) => r.fix.kind === 'poam_open' && r.fix.due.slice(0, 10) === gaps.statusEnd!.date.slice(0, 10));
  const allOnPoam = gaps.notMetPoam.length > 0 && gaps.notMetPoam.every((r) => r.fix.kind === 'poam_open' || ctx.added.has(r.id));
  return (
    <div className="gp-tab-panel" data-part="cmmc-gaps">
      {/* H12 / G1–G5: one hero per state; an executive glance surface.
          PR #57 R1 aesthetic High #1: stat tiles (big number + small caption) instead of a red
          run-on sentence. Only the single failing tile carries the danger tone; the blocker
          callout stays as its own interactive link beneath, since it jumps into the list. */}
      <section className="gp-hero" aria-labelledby="cmmc-gaps-h" data-part="gaps-hero">
        <h3 id="cmmc-gaps-h" className="gp-cmmc-headline gp-cmmc-headline--stats" tabIndex={-1}>
          {(() => {
            const stats = gapsHeroStats(h);
            if (!stats.length && !h.text) return <span data-part="hero-text">{h.text ?? 'All current'}</span>;
            if (!stats.length) return <span data-part="hero-text">{h.text}</span>;
            return <HeroStats stats={stats} />;
          })()}
        </h3>
        {h.blocked ? (
          <p className="gp-cmmc-blocked-line" data-part="hero-blocked-wrap">
            <a href="#gaps-must" className="gp-cmmc-blocked" data-part="hero-blocked" onClick={scrollTo('gaps-must')}>
              <span className="gp-cmmc-bign">{h.blocked.count}</span>{` ${h.blocked.text} blocking conditional`}{h.blocked.more ? ` · ${h.blocked.more}` : ''}{' →'}
            </a>
          </p>
        ) : null}
        {h.blocked?.extra ? <p className="gp-cmmc-note" data-part="hero-extra">{h.blocked.extra}</p> : null}
        {uploaded.size ? <p className="gp-cmmc-note" role="status" data-part="hero-uploaded">{`${uploaded.size} uploaded, to record`}</p> : null}
        {/* G24: no counts line that only repeats the headline ("4 not met" under "4 open"). */}
        {countLine && !nothingOpen && !(gaps.statusEnd && !c.needEvidence && !c.waiting && !c.stale) ? <p className="gp-cmmc-gates" data-part="gaps-counts">{countLine}</p> : null}
        <div className="gp-actions" data-part="gaps-levers">
          <HeroPrimaryAction g={gaps} ctx={ctx} p={p} />
          {h.then.length ? (
            <span className="gp-cmmc-then" data-part="levers-then">
              <span className="gp-cmmc-note">Then:</span>
              {h.then.map((t) => (t.kind === 'lever'
                ? <LeverAction key={`l-${t.lever.kind}-${t.lever.connectorKind ?? ''}`} lever={t.lever} ctx={ctx} />
                // R3-3: chips that only scroll are quiet text links; chips that run something are buttons.
                : <a key={`r-${t.row.id}`} className="gp-cmmc-quiet" data-part="then-row" href={`#${t.row.anchor}`} onClick={scrollTo(t.row.anchor)}>{t.label}</a>))}
            </span>
          ) : null}
        </div>
      </section>

      {nothingOpen ? <p className="gp-cmmc-note" data-part="gaps-empty">Nothing open · every requirement is judged and current.</p> : <>
        {c.needEvidence + c.waiting ? (
          <section className="gp-sec" aria-labelledby="gaps-ua-h" data-part="gaps-unassessed">
            <h3 id="gaps-ua-h" className="gp-sec-h">{`Not assessed · ${c.needEvidence + c.waiting}`}</h3>
            {gaps.noEvidence.length ? <>
              <h4 className="gp-cmmc-subh" id="gaps-ne" tabIndex={-1}>{`No evidence · ${gaps.noEvidence.length}${ptsOf(gaps.noEvidence)}`}</h4>
              <ul className="gp-cmmc-rows gp-cmmc-gaprows" aria-labelledby="gaps-ne">
                {gaps.noEvidence.map((r) => row(r, <span data-part="missing">{r.missing}</span>, <RowActions r={r} ctx={ctx} />))}
              </ul>
            </> : null}
            {gaps.waiting.length ? <>
              <h4 className="gp-cmmc-subh" id="gaps-aw">{`Awaiting review · ${gaps.waiting.length}${ptsOf(gaps.waiting)}`}</h4>
              <ul className="gp-cmmc-rows gp-cmmc-gaprows" aria-labelledby="gaps-aw">
                {gaps.waiting.map((r) => row(r, r.missing, <RowActions r={r} ctx={ctx} />))}
              </ul>
            </> : null}
          </section>
        ) : null}

        {c.notMet ? (
          <section className="gp-sec" aria-labelledby="gaps-nm-h" data-part="gaps-notmet">
            <h3 id="gaps-nm-h" className="gp-sec-h">{`Not met · ${c.notMet}`}</h3>
            {([[l1 ? 'Must be Met' : 'Must be Met for conditional', gaps.notMetMust, 'gaps-must'], ['POA&M-eligible', gaps.notMetPoam, 'gaps-poam']] as const).map(([title, list, id]) => list.length ? (
              <div key={id}>
                <div className="gp-cmmc-subrow">
                  {/* H-R3-2: every item on the POA&M with the same deadline → the date lives on the group title. */}
                  <h4 className="gp-cmmc-subh" id={id} tabIndex={-1}>{id === 'gaps-poam' && sameEnd
                    ? <>{`On the POA&M · ${list.length} · `}<span className="gp-cmmc-toned" data-tone={gaps.statusEnd!.tone}>{`closes by ${fmtWithYear(gaps.statusEnd!.date, todayDay())}`}</span></>
                    : id === 'gaps-poam' && allOnPoam ? `On the POA&M · ${list.length}`
                    : `${title} · ${list.length}${ptsOf([...list])}`}</h4>
                  {id === 'gaps-poam' ? <AddAllPoam rows={[...list]} ctx={ctx} /> : null}
                </div>
                <ul className="gp-cmmc-rows gp-cmmc-gaprows" aria-labelledby={id}>
                  {list.map((r) => row(r, (
                    <>
                      <b>{r.gap ?? 'Not met'}</b>{r.why && r.why !== r.gap ? ` · ${r.why}` : ''}
                      {r.chain?.length ? (
                        <span className="gp-cmmc-chain" data-part="chain">
                          {/* NEW-4: the failing cell, linked; no trailing "→ Not met". */}
                          {`${r.chain[0]!.where} → `}<Link to={`${base}?view=requirements#${cellId(r.chain[0]!.cellKey)}`}>{r.chain[0]!.tag}</Link>{` ${r.chain[0]!.name}`}
                          {r.chain.length > 1 ? ` · +${r.chain.length - 1} more` : ''}
                        </span>
                      ) : null}
                    </>
                  ), <RowActions r={r} ctx={ctx} />, { notMet: true, tone: id === 'gaps-poam' && r.fix.kind === 'poam_open' ? poamTone ?? undefined : undefined, noPts: id === 'gaps-poam' && (sameEnd || allOnPoam) }))}
                </ul>
              </div>
            ) : null)}
          </section>
        ) : null}

        {gaps.stale.length ? (
          <section className="gp-sec" aria-labelledby="gaps-st-h" data-part="gaps-stale">
            <h3 id="gaps-st-h" className="gp-sec-h">{`Met, but stale · ${gaps.stale.length}`}</h3>
            <p className="gp-cmmc-note">Counted · blocks finishing until refreshed</p>
            <ul className="gp-cmmc-rows gp-cmmc-gaprows" aria-labelledby="gaps-st-h">
              {gaps.stale.map((r) => row(r, <span data-part="stale-line">{`${r.staleLead} · `}<span className={r.overdue ? 'gp-cmmc-over' : undefined}>{r.staleTail}</span></span>,
                <RowActions r={r} ctx={ctx} />))}
            </ul>
          </section>
        ) : null}
        {empty.length ? <p className="gp-cmmc-note" data-part="gaps-empty">{`${empty.join(' · ').replace(/^./, (x) => x.toUpperCase())}.`}</p> : null}
      </>}
    </div>
  );
}

// ---- Requirements drill-down ------------------------------------------------------------------

type Filter = 'all' | 'not_met' | 'not_assessed' | 'met' | 'stale';
const FILTERS: readonly Filter[] = ['all', 'not_met', 'not_assessed', 'met', 'stale'];
type Sel = { obj: string; cell: string | null; req: string };

const defaultOpen = (r: DrillRequirement, level: DrillModel['level']) => r.status === 'not_met' && r.mustMet && level === '2';

export function CmmcRequirements(p: AssessProps) {
  const { d, drill, gaps } = useModels(p);
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get('filter');
  // F15: an unknown filter is All.
  const filter: Filter = rawFilter && (FILTERS as readonly string[]).includes(rawFilter) ? rawFilter as Filter : 'all';
  const fam = params.get('family') ?? 'all';
  const [sel, setSel] = useState<Sel | null>(null);
  // R2-1: each requirement keeps its own open state (seeded from the default, updated by its toggle).
  const [openMap, setOpenMap] = useState<Readonly<Record<string, boolean>>>({});
  const narrow = useNarrow();
  const opener = useRef<string | null>(null);
  const focusLater = useFocusWhenMounted();
  const ctx = useRowCtx(p, d.records.data?.determinations, () => undefined);
  useHashFocus(!!drill, (id, el) => {
    // F19: landing on #req-… preselects the first failing or missing objective; #cell-… selects that cell.
    if (id.startsWith('req-')) {
      const r = drill?.requirements.find((x) => x.anchor === id);
      const first = r ? firstOpenCell(r) : null;
      if (r && first) { setOpenMap((m) => ({ ...m, [r.id]: true })); setSel({ ...first, req: r.id }); }
    } else if (id.startsWith('cell-')) {
      for (const r of drill?.requirements ?? []) for (const o of r.objectives) for (const c of o.cells) {
        if (cellId(c.key) === id) { opener.current = id; setOpenMap((m) => ({ ...m, [r.id]: true })); setSel({ obj: o.id, cell: c.key, req: r.id }); }
      }
      void el;
    }
  });
  if (!drill) return <Loading q={d.readiness} label="requirements" />;
  const records = new Map((d.records.data?.determinations ?? []).map((x) => [x.determination_id, x] as const));
  const rowsById = new Map<string, GapRow>();
  for (const r of [...(gaps?.notMetMust ?? []), ...(gaps?.notMetPoam ?? []), ...(gaps?.noEvidence ?? []), ...(gaps?.waiting ?? [])]) rowsById.set(r.id, r);
  const staleById = new Map((gaps?.stale ?? []).map((r) => [r.id, r] as const));
  const famOk = fam === 'all' || drill.families.some((f) => f.id === fam);
  const matches = (r: DrillRequirement) => (!famOk || fam === 'all' || r.family === fam) && (filter === 'all'
    || (filter === 'not_met' && r.status === 'not_met') || (filter === 'not_assessed' && (r.status === 'no_evidence' || r.status === 'undetermined'))
    || (filter === 'met' && r.status === 'met') || (filter === 'stale' && r.stale));
  const list = drill.requirements.filter(matches);
  const setParam = (k: string, v: string | null) => { const n = new URLSearchParams(params); if (!v || v === 'all') n.delete(k); else n.set(k, v); setParams(n, { replace: true }); };
  const c = drill.counts;
  // A14: hide 0-count chips except All.
  const F: Array<[Filter, string, number]> = ([['all', 'All', c.total], ['not_met', 'Not met', c.notMet], ['not_assessed', 'Not assessed', c.notAssessed], ['met', 'Met or N/A', c.met], ['stale', 'Stale', c.stale]] as Array<[Filter, string, number]>)
    .filter(([k, , n]) => k === 'all' || n > 0 || k === filter);
  const isOpen = (r: DrillRequirement) => openMap[r.id] ?? defaultOpen(r, drill.level);
  const pick = (r: DrillRequirement, o: DrillObjective, cell: string | null, from: string) => {
    opener.current = from;
    setOpenMap((m) => ({ ...m, [r.id]: true }));
    setSel({ obj: o.id, cell, req: r.id });
    // F10: a cell focuses its record; an objective focuses the panel title.
    focusLater(() => document.getElementById(cell ? 'cmmc-record-h' : 'cmmc-strategy-h'));
  };
  // R2-1: Back returns focus once the opener is mounted again; the requirement stays open.
  const back = () => { const id = opener.current; setSel(null); focusLater(() => (id ? document.getElementById(id) : null)); };
  const selReq = sel ? drill.requirements.find((r) => r.id === sel.req) ?? null : null;
  const selected = selReq && sel ? (() => { const o = selReq.objectives.find((x) => x.id === sel.obj); return o ? { r: selReq, o } : null; })() : null;
  const action = selReq ? (rowsById.get(selReq.id) ?? staleById.get(selReq.id) ?? null) : null;
  const panel = <StrategyPanel p={p} selected={selected} cell={sel?.cell ?? null} records={records} level={drill.level} narrow={narrow} onBack={back} row={action} ctx={ctx} />;
  const l1Tone = drill.level === '1' ? (c.met === c.total ? 'ok' : 'red') : drill.blocked ? 'red' : undefined;
  return (
    <div className="gp-tab-panel" data-part="cmmc-requirements">
      {/* PR #57 R1 aesthetic High #1: stat tiles instead of a run-on red sentence. */}
      <section className="gp-hero" aria-labelledby="cmmc-reqs-h">
        <h3 id="cmmc-reqs-h" className="gp-cmmc-headline gp-cmmc-headline--stats" data-part="reqs-headline">
          <HeroStats stats={drillHeadlineStats(drill)} />
          <span className="gp-sr-only">{drill.level === '1' && l1Tone === 'red' ? 'Level 1 not yet met.' : drill.blocked ? 'Conditional threshold not reached.' : ''}</span>
        </h3>
      </section>
      <div className="gp-cmmc-filters">
        <div role="group" aria-label="Show">
          {F.map(([k, l, n]) => <button key={k} type="button" className="gp-cmmc-fbtn" aria-pressed={filter === k} onClick={() => setParam('filter', k)}>{`${l} `}<b>{n}</b></button>)}
        </div>
        <span className="gp-cmmc-famform">
          <label htmlFor="cmmc-fam">Family</label>
          {/* F15: applies on change and writes &family= to the URL. */}
          <select id="cmmc-fam" value={famOk ? fam : 'all'} onChange={(e) => setParam('family', e.currentTarget.value)}>
            <option value="all">{`All families · ${c.total}`}</option>
            {drill.families.map((f) => <option key={f.id} value={f.id}>{`${f.id} · ${f.count}`}</option>)}
          </select>
        </span>
        {/* F14: the rollup key, quiet. */}
        <details className="gp-cmmc-disclosure gp-cmmc-rollup" data-part="rollup-key">
          <summary>How results roll up</summary>
          <p className="gp-cmmc-note">One Not met asset fails the objective · one failing objective fails the requirement · No evidence or a result awaiting review is Not assessed · N/A counts as Met</p>
        </details>
      </div>
      <div className="gp-cmmc-drill" data-narrow={narrow || undefined}>
        <div className="gp-cmmc-reqwrap">
          <div className="gp-cmmc-subrow">
            <h4 className="gp-cmmc-subh" id="cmmc-reqlist-h">Requirements</h4>
            {/* A11y 9: the count is its own status, not the heading. */}
            <span role="status" className="gp-cmmc-note" data-part="reqs-showing">{`Showing ${list.length} of ${c.total}`}</span>
          </div>
          <ul className="gp-cmmc-reqlist" aria-labelledby="cmmc-reqlist-h">
            {list.map((r) => <RequirementItem key={r.id} r={r} p={p} sel={sel} onPick={pick} level={drill.level} open={isOpen(r)}
              onToggle={(open) => setOpenMap((m) => (m[r.id] === open ? m : { ...m, [r.id]: open }))} panel={narrow && sel?.req === r.id ? panel : null} />)}
            {!list.length ? <li className="gp-dim">Nothing matches.</li> : null}
          </ul>
        </div>
        {narrow ? null : panel}
      </div>
    </div>
  );
}

function StatusMark({ status, label }: { status: keyof typeof STATUS; label?: string }) {
  const s = STATUS[status];
  return <span className="gp-cmmc-res" data-tone={s.tone}><span aria-hidden="true">{s.glyph}</span>{label ?? s.label}</span>;
}

function RequirementItem({ r, p, sel, onPick, level, panel, open, onToggle }: {
  r: DrillRequirement; p: AssessProps; sel: Sel | null; level: DrillModel['level']; panel: ReactNode; open: boolean; onToggle: (open: boolean) => void;
  onPick: (r: DrillRequirement, o: DrillObjective, cell: string | null, from: string) => void;
}) {
  const perAsset = r.objectives.find((o) => !o.orgWide)?.cells ?? [];
  const cols = perAsset.map((c) => ({ id: c.assetId, name: c.assetName }));
  const base = `${p.prefix}/programs/${p.card.program}/prepare`;
  return (
    <li data-status={r.status}>
      <details id={r.anchor} className="gp-cmmc-req" data-status={r.status} data-level={level} open={open} onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>
          <span className="gp-dim gp-cmmc-code">{r.ref}</span>
          <span className="gp-cmmc-reqt">{r.title}{level === '2' && r.mustMet && r.status === 'not_met' ? <span className="gp-cmmc-never">can’t go on POA&amp;M</span> : level === '2' && r.mustMet && r.status !== 'met' ? <span className="gp-cmmc-mm">must-Met · unproven</span> : null}</span>
          {r.points !== null ? <span className="gp-cmmc-pts">{r.points === 0 ? 'required' : `${r.points} pt${r.points === 1 ? '' : 's'}`}</span> : null}
          <span className="gp-cmmc-res" data-tone={r.tone}><span aria-hidden="true">{r.allNA ? '–' : r.stale ? '◷' : STATUS[r.status].glyph}</span>{r.label}</span>
        </summary>
        <div className="gp-cmmc-reqbody">
          <table className="gp-cmmc-mx">
            <caption className="gp-sr-only">{`${r.ref} objectives by asset`}</caption>
            <thead>
              <tr><th scope="col">Objective</th>{cols.length ? cols.map((c) => <th key={c.id ?? 'org'} scope="col">{c.name}</th>) : <th scope="col">Organization</th>}<th scope="col">Objective result</th></tr>
            </thead>
            <tbody>
              {r.objectives.map((o) => {
                const on = sel?.obj === o.id;
                return (
                  <tr key={o.id} aria-current={on ? 'true' : undefined} data-objective={o.id} data-status={o.status}>
                    <th scope="row">
                      <button type="button" id={objId(o.id)} className="gp-cmmc-objbtn" aria-pressed={on && !sel?.cell} onClick={() => onPick(r, o, null, objId(o.id))}
                        aria-label={o.statement ? undefined : `Objective ${o.ref}`}>
                        <span className="gp-cmmc-objtag">{o.tag}</span>{o.statement ? <>{' '}<span className="gp-cmmc-objst">{o.statement}</span></> : null}
                      </button>
                    </th>
                    {o.orgWide
                      // J5-H1: one org-wide judgment = ONE cell, one record.
                      ? <td colSpan={Math.max(1, cols.length)}><span className="gp-cmmc-tdlabel" aria-hidden="true">Org-wide</span><CellButton c={o.cells[0]!} pressed={sel?.cell === o.cells[0]!.key} onClick={() => onPick(r, o, o.cells[0]!.key, cellId(o.cells[0]!.key))} orgWide /></td>
                      : o.cells.map((c) => <td key={c.key}><span className="gp-cmmc-tdlabel" aria-hidden="true">{c.assetName}</span><CellButton c={c} pressed={sel?.cell === c.key} onClick={() => onPick(r, o, c.key, cellId(c.key))} /></td>)}
                    <td><span className="gp-cmmc-tdlabel" aria-hidden="true">Objective result</span><StatusMark status={o.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {panel}
          {r.allNA && r.naRationale ? <p className="gp-cmmc-note" data-part="na-rationale">{`N/A · ${r.naRationale}`}</p> : null}
          {r.status === 'no_evidence' || r.stale ? <p className="gp-cmmc-note"><Link to={`${base}?view=gaps#gap-${r.anchor.slice(4)}`}>{r.stale ? 'Refresh it in Gaps' : 'Collect it in Gaps'}</Link></p> : null}
        </div>
      </details>
    </li>
  );
}

function CellButton({ c, onClick, orgWide, pressed }: { c: DrillCell; onClick: () => void; orgWide?: boolean; pressed?: boolean }) {
  const s = STATUS[c.status];
  const tone = c.proposal ? 'amber' : c.fresh?.stale ? 'neutral' : s.tone;
  return (
    <button type="button" id={cellId(c.key)} className="gp-cmmc-cell" data-tone={tone} data-status={c.status} aria-pressed={!!pressed} onClick={onClick}
      aria-label={`${orgWide ? 'Org-wide' : c.assetName} · ${c.label}${c.gap ? ` · ${c.gap}` : ''} · open the record`}>
      <span aria-hidden="true">{c.proposal ? '◇' : c.fresh?.stale ? '◷' : s.glyph}</span>{orgWide ? `Org-wide · ${c.label}` : c.label}
    </button>
  );
}

function StrategyPanel({ p, selected, cell, records, narrow, onBack, row, ctx }: {
  p: AssessProps; selected: { r: DrillRequirement; o: DrillObjective } | null; cell: string | null; records: ReadonlyMap<string, AssessmentDetermination>;
  level: DrillModel['level']; narrow: boolean; onBack: () => void; row: GapRow | null; ctx: RowCtx;
}) {
  if (!selected) {
    if (narrow) return null;
    return <aside className="gp-cmmc-strategy" aria-labelledby="cmmc-strategy-h" data-part="strategy"><h3 id="cmmc-strategy-h" tabIndex={-1}>Objective details</h3><p className="gp-cmmc-note">Choose an objective or a cell to see how it is checked and its record.</p></aside>;
  }
  const { r, o } = selected;
  const s = o.strategy;
  const c = cell ? o.cells.find((x) => x.key === cell) ?? null : null;
  const rec = c?.determinationId ? records.get(c.determinationId) ?? null : null;
  const base = `${p.prefix}/programs/${p.card.program}/prepare`;
  const backLabel = `Back to ${o.ref}${c ? ` · ${c.assetId ? c.assetName : 'Org-wide'}` : ''}`;
  const dl = s ? (
    <dl className="gp-cmmc-dl" data-part="strategy-dl">
      {/* NEW-6: "What's checked" only when it says more than the objective's own statement (the title). */}
      {s.checked && s.checked !== o.statement ? <><dt>What’s checked</dt><dd>{s.checked}</dd></> : null}
      <dt>Met when</dt><dd>{s.metWhen}</dd>
      <dt>How</dt><dd>{s.how}</dd>
      <dt>Sources</dt><dd>{s.sources.length ? s.sources.join(' · ') : 'None named yet'}</dd>
      <dt>Window or sample</dt><dd>{s.window}</dd>
      <dt>Scope</dt><dd>{s.scope}</dd>
      <dt>Method</dt><dd>{s.method}</dd>
      {s.typicalGap ? <><dt>Typical gap</dt><dd>{s.typicalGap}</dd></> : null}
    </dl>
  ) : <p className="gp-cmmc-note">No test is mapped to this objective yet.</p>;
  // A cell's record carries its Gaps row's action when the cell is Not met or stale (N1).
  const act = c && row && (c.status === 'not_met' || c.fresh?.stale) ? row : null;
  return (
    <aside className="gp-cmmc-strategy" aria-labelledby="cmmc-strategy-h" data-part="strategy" data-inline={narrow || undefined}
      // R2-1: Escape returns to the cell (both layouts); an Escape a form inside already handled is ignored.
      onKeyDown={(e) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onBack(); } }}>
      <button type="button" className="gp-cmmc-quiet gp-cmmc-backbtn" onClick={onBack} data-part="panel-back">{`← ${backLabel}`}</button>
      <h3 id="cmmc-strategy-h" tabIndex={-1}>{`${o.ref} · ${o.statement || r.title}`}</h3>
      {c ? <RecordView c={c} rec={rec} p={p} r={r} o={o} base={base} action={act} ctx={ctx} records={records} /> : null}
      {narrow && c ? <details className="gp-cmmc-disclosure" data-part="how-checked"><summary>How it’s checked</summary>{dl}</details> : dl}
      {s || rec ? (
        <details className="gp-cmmc-disclosure" data-part="strategy-verify">
          <summary>Verify</summary>
          <dl className="gp-cmmc-verify">
            {(s?.verify ?? []).map((v) => <Fragment key={v.testKey}><dt>{v.role === 'primary' ? 'Test' : 'Also'}</dt><dd><code>{v.testKey}</code>{` · version ${v.version ?? '—'} · ${v.basis === 'pinned' ? 'pinned' : 'current'}`}{v.hash ? <> · <code>{v.hash}</code></> : null}</dd></Fragment>)}
            {rec ? <><dt>Record</dt><dd><code>{rec.determination_id}</code>{rec.test_version_id ? <> · judged on <code>{rec.test_version_id}</code></> : null}</dd></> : null}
          </dl>
        </details>
      ) : null}
    </aside>
  );
}

const who = (p: AssessProps, id: string | null | undefined) => (id ? p.names?.get(id) ?? 'A team member' : null);

/** Evidence named and linked: documents by the server's href; a check when the library lists it. */
function EvidenceList({ p, evidence }: { p: AssessProps; evidence: readonly ResolvedEvidenceRef[] }) {
  const lib = useQuery({ queryKey: ['evidence-unified', p.slug, 'cited'], queryFn: () => getUnifiedEvidence(p.slug, { limit: 200 }), enabled: evidence.some((e) => !e.href && e.kind === 'check'), retry: false, staleTime: 60_000 });
  const listed = new Set((lib.data?.evidence ?? []).map((e) => e.id));
  if (!evidence.length) return <>None cited</>;
  return (
    <ul className="gp-cmmc-cited">
      {evidence.map((e) => {
        const href = e.href ?? (e.kind === 'check' && listed.has(e.id) ? `/evidence?item=${encodeURIComponent(e.id)}&kind=check` : null);
        return <li key={`${e.kind}:${e.id}`}>{href ? <Link to={`${p.prefix}${href}`}>{e.title ?? 'Evidence'}</Link> : e.title ?? 'Evidence not found'}</li>;
      })}
    </ul>
  );
}

function RecordView({ c, rec, p, r, o, base, action, ctx, records }: {
  c: DrillCell; rec: AssessmentDetermination | null; p: AssessProps; r: DrillRequirement; o: DrillObjective; base: string; action: GapRow | null; ctx: RowCtx;
  records: ReadonlyMap<string, AssessmentDetermination>;
}) {
  const where = c.assetId ? c.assetName : 'Org-wide';
  const today = todayDay();
  // V102-8-5: the cell's history (open proposal, counted result, a send-back) drives the record panel.
  const history = cellHistory(records.values() as Iterable<RecordRow>, o.id, c.assetId);
  const counted = history.reviewed ?? (rec as RecordRow | null);
  const evidence = (counted as RecordRow | null)?.evidence ?? [];
  const upload = p.canUpload ? <UploadForObjective slug={p.slug} program={p.card.program} periodId={p.periodId} refLabel={o.ref} objectives={[{ id: o.id, ref: o.ref }]} label={`Upload for ${o.ref}`} /> : null;
  const reviewedBody = counted ? (
    <>
      <dl className="gp-cmmc-dl">
        {counted.gap_type && counted.gap_type !== 'no_evidence' ? <><dt>Gap</dt><dd>{c.gap ?? counted.gap_type}</dd></> : null}
        {counted.rationale ? <><dt>{counted.result === 'not_applicable' ? 'Why N/A' : 'Why'}</dt><dd>{counted.rationale}</dd></> : null}
      </dl>
      {/* N1: the same action as the requirement's Gaps row, right under "Why". */}
      {action ? <p className="gp-cmmc-recact" data-part="record-action"><RowActions r={action} ctx={ctx} /><FindingsLink r={action} ctx={ctx} /></p> : null}
      <dl className="gp-cmmc-dl">
        <dt>Recorded by</dt><dd>{`${who(p, counted.proposed_by)} · ${fmtWithYear(counted.proposed_at, today)}${c.fresh?.suffix ? ` · ${c.fresh.suffix}` : ''}`}</dd>
        <dt>Reviewed by</dt><dd>{counted.reviewed_at ? `${who(p, counted.reviewed_by)} · ${fmtWithYear(counted.reviewed_at, today)}` : 'Awaiting review'}</dd>
        <dt>Evidence</dt><dd><EvidenceList p={p} evidence={evidence as ResolvedEvidenceRef[]} /></dd>
      </dl>
    </>
  ) : null;
  const emptyBody = c.status === 'no_evidence'
    ? <><p className="gp-cmmc-note">No evidence yet · counts as not assessed.</p>
        {upload ?? <Link className="gp-cmmc-quiet" to={`${base}?view=gaps#gap-${r.anchor.slice(4)}`}>Collect it in Gaps</Link>}</>
    : <p className="gp-cmmc-note">{'Nothing recorded yet · '}<Link className="gp-cmmc-quiet" to={`${base}?view=gaps#gap-${r.anchor.slice(4)}`}>See it in Gaps</Link></p>;
  return (
    <CellRecord slug={p.slug} program={p.card.program} periodId={p.periodId} prefix={p.prefix} isAdmin={p.isAdmin} objective={{ id: o.id, ref: o.ref }} assetId={c.assetId} where={where}
      history={{ ...history, reviewed: history.reviewed ?? (rec as RecordRow | null) }} viewerId={p.viewerId} names={p.names}
      emptyLabel={c.label} reviewedBody={reviewedBody} emptyBody={emptyBody} uploadSlot={upload} />
  );
}

// ---- the fix for each requirement, elsewhere (Findings empty state) ---------------------------------

/**
 * F1 / N3: the Findings page, filtered to requirements with no open finding,
 * lists each requirement with its own next step (by gap type) — never a link
 * back to where the user came from. Level 1: a requirement outside Level 1 says so.
 */
export function RequirementFix({ slug, prefix, card, periodId, refs, role, viewerId, names }: {
  slug: string; prefix: string; card: ProgramCard; periodId: string; refs: readonly string[]; role: string; viewerId: string; names?: ReadonlyMap<string, string>;
}) {
  const isAdmin = role === 'owner' || role === 'admin';
  const p: AssessProps = { slug, prefix, card, periodId, periodLabel: '', canUpload: isAdmin || role === 'editor', canManage: isAdmin, isAdmin, viewerId, names };
  const { d, drill, gaps } = useModels(p);
  const ctx = useRowCtx(p, d.records.data?.determinations, () => undefined);
  if (!drill || !gaps) return d.readiness.isLoading ? <Spinner label="Loading the next step" /> : null;
  const all = [...gaps.notMetMust, ...gaps.notMetPoam, ...gaps.noEvidence, ...gaps.waiting, ...gaps.stale];
  const items = refs.map((ref) => {
    const outside = drill.level === '1' && !drill.requirements.some((r) => r.l2Refs.includes(ref));
    return { ref, outside, row: outside ? null : all.find((r) => r.l2Refs.includes(ref)) ?? null };
  });
  const one = (it: typeof items[number]) => (it.outside
    ? <span className="gp-cmmc-note" data-part="not-in-level">{`${it.ref} isn’t part of Level 1`}</span>
    : it.row ? <span className="gp-cmmc-act" data-part="requirement-fix"><FixAction fix={it.row.fix} r={it.row} ctx={ctx} primary={items.length === 1} label={items.length === 1 ? undefined : heroFixLabel(it.row)} /></span>
      : <span className="gp-cmmc-note" data-part="nothing-open">{`${it.ref} has nothing open`}</span>);
  if (items.length === 1) return one(items[0]!);
  return (
    <ul className="gp-cmmc-rows" data-part="requirement-fixes" aria-label="Next step for each requirement">
      {items.map((it) => <li key={it.ref}><span className="gp-cmmc-name">{it.row?.title ?? it.ref}<span className="gp-dim gp-cmmc-code">{it.ref}</span></span>{one(it)}</li>)}
    </ul>
  );
}
